import { useEffect, useMemo, useState } from "react";
import s from "./ShigiraConciergeDemoPage.module.scss";

const activityUrl = "https://act.shigira.com/top/neighborhood?lng=ja-JP";
const restaurantUrl = "https://shigira.com/restaurant/";
const resortUrl = "https://shigira.com/";

const weatherLabels = {
  0: ["快晴", "☀️"], 1: ["晴れ", "🌤️"], 2: ["晴れ時々くもり", "🌤️"],
  3: ["くもり", "☁️"], 45: ["霧", "🌫️"], 48: ["霧", "🌫️"],
  51: ["小雨", "🌦️"], 53: ["小雨", "🌦️"], 55: ["雨", "🌧️"],
  61: ["雨", "🌧️"], 63: ["雨", "🌧️"], 65: ["強い雨", "🌧️"],
  80: ["にわか雨", "🌦️"], 81: ["にわか雨", "🌧️"], 82: ["強い雨", "⛈️"],
  95: ["雷雨", "⛈️"], 96: ["雷雨", "⛈️"], 99: ["雷雨", "⛈️"],
};

const fallback = {
  weather: "晴れ時々くもり", icon: "🌤️", high: 29, low: 25,
  rain: 30, wind: 4.2, windDirection: "北東", sunset: "18:30",
};

const windDirection = (degree) => {
  const labels = ["北", "北東", "東", "南東", "南", "南西", "西", "北西"];
  return Number.isFinite(degree) ? labels[Math.round(degree / 45) % 8] : "--";
};

const formatTime = (value) => String(value || "").split("T")[1]?.slice(0, 5) || "--:--";

const buildToday = (raw) => {
  const daily = raw?.weather?.daily;
  const current = raw?.weather?.current;
  if (!daily?.time?.length) return fallback;

  const todayKey = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
  const index = Math.max(0, daily.time.indexOf(todayKey));
  const code = Number(daily.weather_code?.[index]);
  const [weather, icon] = weatherLabels[code] || [fallback.weather, fallback.icon];

  return {
    weather,
    icon,
    high: Math.round(Number(daily.temperature_2m_max?.[index])) || fallback.high,
    low: Math.round(Number(daily.temperature_2m_min?.[index])) || fallback.low,
    rain: Math.round(Number(daily.precipitation_probability_max?.[index])) || 0,
    wind: Number(current?.wind_speed_10m) || fallback.wind,
    windDirection: windDirection(Number(current?.wind_direction_10m)),
    sunset: formatTime(daily.sunset?.[index]) || fallback.sunset,
  };
};

const ShigiraConciergeDemoPage = () => {
  const [today, setToday] = useState(fallback);
  const [profile, setProfile] = useState("couple");

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ""}/ocean-data-cache.json`, { cache: "no-store" })
      .then((response) => response.json())
      .then((data) => setToday(buildToday(data)))
      .catch(() => setToday(fallback));
  }, []);

  const rainy = today.rain >= 50;
  const windy = today.wind >= 6;
  const recommendations = {
    couple: {
      eyebrow: "おふたりの滞在におすすめ",
      title: "今から、貸切カヤックへ",
      text: "朝の穏やかな時間と現在地から、10:00の貸切枠を選びました。終了後のランチにも間に合います。",
      meta: ["10:00枠 残り2組", "ホテルから徒歩6分", "会員特典あり"],
      icon: "🛶",
    },
    family: {
      eyebrow: "お子さまと一緒におすすめ",
      title: "ウミガメを探すビーチ時間",
      text: "移動が少なく、休憩場所にも近いプランです。混雑が増える前の時間をご案内します。",
      meta: ["対象年齢 4歳〜", "空きあり", "タオル貸出あり"],
      icon: "🐢",
    },
    relax: {
      eyebrow: "ゆっくり過ごすなら",
      title: "黄金温泉と早めのランチへ",
      text: "屋外の混雑を避けて、温泉から空席のあるレストランへ。移動時間も含めて組みました。",
      meta: ["待ち時間なし", "11:30予約可", "巡回バス利用"],
      icon: "♨️",
    },
  };
  const recommendation = recommendations[profile];

  const schedule = useMemo(() => [
    {
      time: "8:00–10:00", icon: "🏝️", tag: "MORNING",
      title: windy ? "風を避けられるビーチへ" : "シギラビーチで朝の海遊び",
      text: windy
        ? "風向きを確認し、スタッフ案内のもと穏やかな場所を選びましょう。"
        : "比較的風が弱い朝の時間に、ビーチ散策やシュノーケルがおすすめです。",
      action: "体験を予約", href: activityUrl,
    },
    {
      time: "11:30–13:30", icon: "🍽️", tag: "LUNCH",
      title: "今日の気分でリゾートランチ",
      text: "現在地と空席を見ながら、リゾート内レストランを選べます。",
      action: "レストランを見る", href: restaurantUrl,
    },
    {
      time: "14:00–16:30", icon: rainy ? "♨️" : "🛶", tag: "AFTERNOON",
      title: rainy ? "雨の時間は温泉でゆっくり" : "午後のアクティビティ",
      text: rainy
        ? "天候に左右されにくい黄金温泉や館内体験へ切り替えましょう。"
        : "当日予約できるカヤックやSUPを、空き状況から選べます。",
      action: rainy ? "リゾート施設を見る" : "空きを確認", href: rainy ? resortUrl : activityUrl,
    },
    {
      time: `${today.sunset}ごろ`, icon: "🌅", tag: "SUNSET",
      title: "海辺でサンセット",
      text: "日の入り20分前を目安に移動。空模様を確認しながら楽しみましょう。",
      action: "リゾートMAP", href: resortUrl,
    },
  ], [rainy, today.sunset, windy]);

  return (
    <main className={s.root}>
      <header className={s.appHeader}>
        <div>
          <span>CONCEPT DEMO</span>
          <strong>SHIGIRA Stay Guide</strong>
        </div>
        <button type="button" aria-label="language">JP⌄</button>
      </header>

      <section className={s.hero}>
        <div className={s.heroShade} />
        <div className={s.heroContent}>
          <p>GOOD MORNING</p>
          <h1>今日の宮古島を、<br />いちばん心地よく。</h1>
          <div className={s.weatherPill}>
            <span>{today.icon}</span>
            <div><strong>{today.weather}</strong><small>{today.high}℃ / {today.low}℃</small></div>
          </div>
        </div>
      </section>

      <section className={s.statusCard}>
        <div className={s.statusLead}>
          <span className={s.good}>YOUR STAY</span>
          <h2>おはようございます、田中様</h2>
          <p>ミラージュご宿泊2日目・チェックアウトまであと2日</p>
        </div>
        <div className={s.statusGrid}>
          <div><span>降水確率</span><strong>{today.rain}%</strong></div>
          <div><span>風</span><strong>{today.windDirection} {today.wind.toFixed(1)}m/s</strong></div>
          <div><span>日の入り</span><strong>{today.sunset}</strong></div>
        </div>
      </section>

      <section className={s.personalSection}>
        <div className={s.sectionHead}>
          <div><span>NEXT BEST ACTION</span><h2>今から、何する？</h2></div>
          <small>予約・現在地・空席から提案</small>
        </div>
        <div className={s.profileTabs} aria-label="過ごし方を選択">
          <button className={profile === "couple" ? s.selectedProfile : ""} onClick={() => setProfile("couple")} type="button">ふたりで</button>
          <button className={profile === "family" ? s.selectedProfile : ""} onClick={() => setProfile("family")} type="button">子ども連れ</button>
          <button className={profile === "relax" ? s.selectedProfile : ""} onClick={() => setProfile("relax")} type="button">ゆっくり</button>
        </div>
        <article className={s.nextBestCard}>
          <div className={s.nextBestVisual}><span>{recommendation.icon}</span></div>
          <div className={s.nextBestBody}>
            <small>{recommendation.eyebrow}</small>
            <h3>{recommendation.title}</h3>
            <p>{recommendation.text}</p>
            <div className={s.reasonChips}>{recommendation.meta.map((item) => <span key={item}>{item}</span>)}</div>
            <a href={activityUrl} target="_blank" rel="noreferrer">このプランを予約する <b>→</b></a>
          </div>
        </article>
        <div className={s.liveGrid}>
          <div><span>🚌 次のシャトル</span><strong>あと8分</strong><small>ホテル前 9:42発</small></div>
          <div><span>🏖️ シギラビーチ</span><strong>遊泳可・空いています</strong><small>現地スタッフ更新 5分前</small></div>
          <div><span>🍽️ ランチ</span><strong>11:30 空席あり</strong><small>予約すると待ち時間なし</small></div>
        </div>
      </section>

      <section className={s.section}>
        <div className={s.sectionHead}>
          <div><span>MY DAY</span><h2>予約と移動をまとめた今日の日程</h2></div>
          <small>変更があれば自動で組み直し</small>
        </div>
        <div className={s.timeline}>
          {schedule.map((item) => (
            <article key={item.tag}>
              <div className={s.time}><small>{item.tag}</small><strong>{item.time}</strong></div>
              <div className={s.planIcon}>{item.icon}</div>
              <div className={s.planBody}>
                <h3>{item.title}</h3><p>{item.text}</p>
                <a href={item.href} target="_blank" rel="noreferrer">{item.action}<span>→</span></a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={`${s.section} ${s.bookingSection}`}>
        <div className={s.sectionHead}><div><span>BOOK & EXPLORE</span><h2>そのまま予約・移動</h2></div></div>
        <div className={s.bookingGrid}>
          <a href={activityUrl} target="_blank" rel="noreferrer"><span>🤿</span><strong>アクティビティ</strong><small>今日参加できる体験</small></a>
          <a href={restaurantUrl} target="_blank" rel="noreferrer"><span>🍽️</span><strong>レストラン</strong><small>食事と空席を確認</small></a>
          <a href={resortUrl} target="_blank" rel="noreferrer"><span>🚌</span><strong>リゾートMAP</strong><small>施設と移動を確認</small></a>
          <a href={resortUrl} target="_blank" rel="noreferrer"><span>🎟️</span><strong>会員・クーポン</strong><small>公式サービスへ</small></a>
        </div>
      </section>

      <section className={s.valuePanel}>
        <span>FOR RESORT OPERATION</span>
        <h2>天気予報ではなく、<br />リゾート全体の意思決定エンジン。</h2>
        <div>
          <p><b>01</b> 宿泊者属性・予約・現在地から個別提案</p>
          <p><b>02</b> 空席・バス・施設状況を含めて即時判断</p>
          <p><b>03</b> 既存予約へ送り、売上と体験価値を向上</p>
        </div>
      </section>

      <footer className={s.demoNotice}>
        <strong>提案用コンセプトデモ</strong>
        <p>シギラセブンマイルズリゾートの公式サービスではありません。既存アプリへの機能追加を想定したUIデモです。</p>
      </footer>

      <nav className={s.bottomNav} aria-label="demo navigation">
        <button type="button" className={s.active}><span>☀︎</span>今日</button>
        <button type="button"><span>⌖</span>マップ</button>
        <button type="button"><span>◫</span>予約</button>
        <button type="button"><span>♙</span>会員</button>
      </nav>
    </main>
  );
};

export default ShigiraConciergeDemoPage;
